import { useLazyQuery } from '@apollo/client/react';
import { useAtom } from 'jotai';
import { useEffect, useMemo, useRef } from 'react';

import { ScrollBox } from '~/components/blocks';
import { GetKarteHistoriesDocument } from '~/graphql/generated/graphql';
import { getNode } from '~/graphql/utility';
import { usePreviousValue } from '~/hooks/use-previous-value';
import { historyPageInfoState } from '~/state/partials/telemedicine_patient_profile/atoms';

export const useFetchHistories = (patientId?: string | null) => {
  const scrollRef = useRef<RefAttributeType<typeof ScrollBox> | null>(null);
  const [pageInfo, setPageInfo] = useAtom(historyPageInfoState);
  const [getHistories, { called, loading, data }] = useLazyQuery(GetKarteHistoriesDocument, {
    fetchPolicy: 'network-only',
  });

  const prevData = usePreviousValue(data);
  const currentData = data || prevData;
  const histories = useMemo(
    () => (currentData ? getNode(data, 'Patient')?.appointments.nodes || [] : []),
    [currentData, data],
  );

  useEffect(() => {
    if (patientId) {
      getHistories({
        variables: {
          id: patientId,
          page: pageInfo.page,
          perPage: pageInfo.perPage,
        },
      });
    }
  }, [getHistories, pageInfo.page, pageInfo.perPage, patientId]);

  useEffect(() => {
    if (!data) return;

    const patient = getNode(data, 'Patient');
    setPageInfo((_state) => ({ ..._state, totalPage: patient?.appointments.pagesCount ?? 1 }));
    scrollRef.current?.toTop();
  }, [data, setPageInfo]);

  return {
    loading: !called || loading,
    scrollRef,
    histories,
  };
};
